export type PlanId = 'trial' | 'starter' | 'growth' | 'scale' | 'enterprise' | 'expired';
export type BillingCycle = 'monthly' | 'annual';

export type FeatureKey =
  | 'paid_media'
  | 'multi_touch_attribution'
  | 'coupon_influencer'
  | 'whatsapp_campaigns'
  | 'experiments'
  | 'acquisition_roi'
  | 'offline_attribution'
  | 'customer_segmentation'
  | 'rfm_auto_assign'
  | 'funnel_retention'
  | 'user_paths'
  | 'customer_profiles'
  | 'predictive_ltv'
  | 'identity_resolution'
  | 'pnl_dashboard'
  | 'inventory_health'
  | 'branch_performance'
  | 'cod_reconciliation'
  | 'true_cost'
  | 'discount_dependency'
  | 'bundles_performance'
  | 'delivery_attempts'
  | 'dashboard_builder'
  | 'semantic_layer'
  | 'calculated_fields'
  | 'custom_joins'
  | 'filter_presets'
  | 'metric_definitions'
  | 'dimension_definitions'
  | 'report_scheduling'
  | 'saved_views'
  | 'api_access'
  | 'white_label'
  | 'verticals'
  | 'sso_saml'
  | 'sla_guarantee'
  | 'dedicated_compute'
  | 'custom_connectors';

export const TRIAL_DAYS = 14;

export const PLAN_NAMES: Record<string, string> = {
  trial:      'Free Trial',
  starter:    'Starter',
  growth:     'Growth',
  scale:      'Scale',
  enterprise: 'Enterprise',
  expired:    'Expired',
};

// USD is primary — matches Stripe product catalogue exactly
export const PLAN_PRICES_USD = {
  starter:    { monthly: 99,    annual: 990    },
  growth:     { monthly: 299,   annual: 2_990  },
  scale:      { monthly: 599,   annual: 5_990  },
  enterprise: { monthly: null,  annual: null   },
} as const;

// SAR secondary (approx at 3.75 rate, rounded)
export const PLAN_PRICES_SAR = {
  starter:    { monthly: 370,   annual: 3_700  },
  growth:     { monthly: 1_120, annual: 11_200 },
  scale:      { monthly: 2_250, annual: 22_450 },
  enterprise: { monthly: null,  annual: null   },
} as const;

// Keep PLAN_PRICING for backward compat (CheckoutModal uses this)
export const PLAN_PRICING: Record<string, { usd: number | null; sar: number | null }> = {
  trial:      { usd: 0,    sar: 0      },
  starter:    { usd: 99,   sar: 370    },
  growth:     { usd: 299,  sar: 1_120  },
  scale:      { usd: 599,  sar: 2_250  },
  enterprise: { usd: null, sar: null   },
  expired:    { usd: 0,    sar: 0      },
};

export interface PlanLimits {
  records: number;     // monthly tracked-customer (MTU) cap — the value meter (name kept for compat)
  dataPoints: number;  // monthly data-point (inbound event) cap — fair-use ceiling
  aiQueries: number;
  retentionDays: number;
  seats: number;
}

// Source of truth for enforcement is bi_plan_limits in the DB; keep these in sync.
export const PLAN_LIMITS: Record<string, PlanLimits> = {
  trial:      { records: 50_000,   dataPoints: 1_000_000,  aiQueries: 150,      retentionDays: 365,      seats: 8         },
  starter:    { records: 50_000,   dataPoints: 1_000_000,  aiQueries: 25,       retentionDays: 180,      seats: 3         },
  growth:     { records: 150_000,  dataPoints: 3_000_000,  aiQueries: 150,      retentionDays: 365,      seats: 8         },
  scale:      { records: 300_000,  dataPoints: 6_000_000,  aiQueries: 750,      retentionDays: 730,      seats: 20        },
  enterprise: { records: Infinity, dataPoints: Infinity,   aiQueries: Infinity, retentionDays: Infinity, seats: Infinity  },
  expired:    { records: 0,        dataPoints: 0,          aiQueries: 0,        retentionDays: 30,       seats: 1         },
};

export const OVERAGE_RATES_SAR = {
  starter: { per10kRecords: 19, per10Queries: 9 },
  growth:  { per10kRecords: 12, per10Queries: 7 },
  scale:   { per10kRecords: 6,  per10Queries: 5 },
} as const;

export const ENTERPRISE_ONLY_FEATURES = ['white_label', 'sso_saml', 'sla_guarantee', 'dedicated_compute', 'custom_connectors'] as const;

export const PLAN_FEATURES: Record<string, string[]> = {
  starter: [
    '50,000 tracked customers (MTU)',
    '1M data points/month',
    '3 team seats',
    '25 AI queries/month',
    '6 months data retention',
    'All analytics dashboards',
    'All data connectors',
  ],
  growth: [
    '150,000 tracked customers (MTU)',
    '3M data points/month',
    '8 team seats',
    '150 AI queries/month',
    '12 months data retention',
    'All analytics dashboards',
    'Overage pricing available',
  ],
  scale: [
    '300,000 tracked customers (MTU)',
    '6M data points/month',
    '20 team seats',
    '750 AI queries/month',
    '24 months data retention',
    'Priority support',
    'Overage pricing available',
  ],
  enterprise: [
    'Sub-tiers: Light ($1K) → Plus ($3K) → Max ($8K) → Custom',
    'Unlimited tracked customers, data points, seats & AI queries',
    'Custom data retention (up to 7 years)',
    'White-label & custom domain',
    'SSO / SAML · MENA data residency available',
    'SLA guarantee · DPA · SOC 2 in progress',
    'Federation / BYO data warehouse option',
    'Custom connector development',
  ],
};

export const UPGRADEABLE_PLANS: PlanId[] = ['starter', 'growth', 'scale', 'enterprise'];

// All features are open to everyone — only enterprise-only features are gated
export function hasFeature(plan: PlanId, feature: FeatureKey, bypass = false): boolean {
  if (bypass) return true;
  const isEnterpriseOnly = (ENTERPRISE_ONLY_FEATURES as readonly string[]).includes(feature);
  if (isEnterpriseOnly) return plan === 'enterprise';
  // Trial and paid plans all have full feature access
  return plan !== 'expired';
}

export function getMinPlanForFeature(feature: FeatureKey): PlanId {
  const isEnterpriseOnly = (ENTERPRISE_ONLY_FEATURES as readonly string[]).includes(feature);
  return isEnterpriseOnly ? 'enterprise' : 'starter';
}

export function getNextPlan(current: PlanId): PlanId | null {
  const order: PlanId[] = ['starter', 'growth', 'scale', 'enterprise'];
  const idx = order.indexOf(current);
  if (idx === -1) return 'starter';
  return idx < order.length - 1 ? order[idx + 1] : null;
}
